<template>
  <div class="page-wrapper">
    <loading v-model:active="isLoading" :is-full-page="true" />

    <vue-final-modal v-model="showModal" classes="modal-container" content-class="modal-content">
      <button class="modal__close" @click="showModal = false">
        <i class="far fa-times-circle"></i>
      </button>
      <div class="modal__content">
        <SurveySummary :assessmentDate="assessmentDate" :question-list="questions" />
      </div>
    </vue-final-modal>

    <ConsentFormModals 
      ref="consentModals"
      :show-consent-modal="showConsentModal"
      :show-email-modal="showEmailModal"
      :consent-form="consentForm"
      :current-row-system-type="currentRowSystemType"
      :email-to-send="emailToSend"
      @update:showConsentModal="showConsentModal = $event"
      @update:showEmailModal="showEmailModal = $event"
      @update:consentForm="consentForm = $event"
      @update:emailToSend="emailToSend = $event"
    />

    <div class="content-wrapper">
      <h1>Referrals </h1>

      <div class="filters">
        <div class="filter-group">
          <label>Start Date</label>
          <datepicker class="input-picker" v-model="startDate" />
        </div>

        <div class="filter-group">
          <label>End Date</label>
          <datepicker class="input-picker" v-model="endDate" />
        </div>

        <div class="filter-group">
          <label>Class</label>
          <select v-model="surveyClass">
            <option value="0">All</option>
            <option value="1">Class 1</option>
            <option value="2">Class 2</option>
            <option value="3">Class 3</option>
          </select>
        </div>

        <div class="filter-group">
          <label>Referrer Email</label>
          <input type="email" v-model="email" />
        </div>

        <div class="filter-group">
          <label>Reference</label>
          <input type="reference" v-model="referenceNo" />
        </div>

        <div class="filter-group">
          <label>Experience</label>
          <select v-model="experience">
            <option value="">All</option>
            <option value="Student">Student</option>
            <option value="Qualified Dentist">Qualified Dentist</option>
          </select>
        </div>

        <div class="filter-group" v-if="experience == 'Student'">
          <label>Undergraduate Year</label>
          <select v-model="undergraduateYear">
            <option value="">All</option>
            <option value="1">1</option>
            <option value="2">2</option>
            <option value="3">3</option>
            <option value="4">4</option>
            <option value="5">5</option>
          </select>
        </div>

        <div class="filter-group" v-if="experience == 'Qualified Dentist'">
          <label>Year of BDS graduation (or equivalent)</label>
          <input type="number" v-model="graduationYear" maxlength="4"
            oninput="javascript: if (this.value.length > this.maxLength) this.value = this.value.slice(0, this.maxLength);" />
        </div>

        <div class="filter-group" v-if="experience == 'Qualified Dentist'">
          <label>Country of undergraduate qualification</label>
          <input type="text" v-model="countryOfQualification" />
        </div>

        <div class="filter-group" v-if="experience == 'Qualified Dentist'">
          <label>Main area of practice</label>
          <select v-model="areaOfPractice">
            <option value="">All</option>
            <option value="Private practice">Private practice</option>
            <option value="NHS general practice">NHS general practice</option>
            <option value="NHS community/salaried dental service">NHS community/salaried dental service</option>
            <option value="Hospital dental service">Hospital dental service</option>
          </select>
        </div>

        <div class="filter-group" v-if="experience == 'Qualified Dentist'">
          <label>Postgraduate training in endodontics? </label>
          <select v-model="havePostgraduateEndodontics">
            <option value="">All</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        </div>

        <div class="filter-group" v-if="havePostgraduateEndodontics == 'true'">
          <label>Postgraduate training in endodontics? </label>
          <select v-model="postgraduateEndodonticsExperience">
            <option value="">All</option>
            <option value="Specialist">Specialist</option>
            <option value="Special interest in Endodontics">Special interest in Endodontics (e.g. MSc, Diploma)</option>
            <option value="Specialty Trainee (or equivalent)">Specialty Trainee (or equivalent)</option>
            <option value="Other">Other</option>
          </select>
        </div>

        <div class="filter-group"
          v-if="havePostgraduateEndodontics == 'true' && postgraduateEndodonticsExperience == 'Other'">
          <label>Other</label>
          <input type="text" v-model="postgraduateEndodonticsExperienceOther" placeholder="Other..." />
        </div>

        <div v-if="havePostgraduateEndodontics == 'true'">
          <div class="filter-group" v-if="postgraduateEndodonticsExperience == 'Specialty Trainee (or equivalent)'">
            <label>Endodontics Year</label>
            <select v-model="postgraduateEndodonticsYear">
              <option value="">All</option>
              <option value="1">1</option>
              <option value="2">2</option>
              <option value="3">3</option>
              <option value="4">4</option>
              <option value="5">5</option>
            </select>
          </div>
          <div v-else>
            <div class="filter-group">
              <label>{{ postgraduateEndodonticsExperience == 'Other' ? "Year of qualification" : "Year of graduation"
              }}</label>
              <input type="number" v-model="postgraduateEndodonticsYear" maxlength="4"
                oninput="javascript: if (this.value.length > this.maxLength) this.value = this.value.slice(0, this.maxLength);" />
            </div>
          </div>
        </div>

        <div class="filter-group">
          <label>Simulated</label>
          <select v-model="isSimulated">
            <option value="-1">All</option>
            <option value="1">Simulated Patient</option>
            <option value="0">Real Patient</option>
          </select>
        </div>
      </div>

      <div style="margin: 10px;">
        <button class="btn" v-on:click="search()">Search</button>
      </div>

      <div class="table">
        <vue-good-table :columns="columns" :rows="rows" :pagination-options="{ enabled: true }">
          <template #table-row="props">
            <span v-if="props.column.field == 'btn'">
              <button class="btn" v-on:click="navToSurvey(props.row.surveyId)">View</button>
            </span>
            <span v-if="props.column.field == 'downloadSurvey'">
              <button class="btn" v-on:click="downloadSurvey(props.row.surveyId)">Download</button>
            </span>
            <span v-if="props.column.field == 'downloadConsent'">
              <button class="btn" v-on:click="$refs.consentModals.openConsentModal(props.row)">Create</button>
            </span>
            <span v-if="props.column.field == 'sendLeaflet'">
              <button class="btn" v-on:click="sendInformation(props.row)">Send Info</button>
            </span>
          </template>
        </vue-good-table>
      </div>

    </div>

  </div>
</template>

<script>
import SurveySummary from '../../../components/SurveySummary.vue';
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';
import authStore from "../../../store/auth-store";
import moment from 'moment'
import { VueGoodTable } from 'vue-good-table-next';
import 'vue-good-table-next/dist/vue-good-table-next.css'

import Datepicker from 'vue3-datepicker'
import ConsentFormModals from '@/components/consent/ConsentFormModals.vue';

const startDate = new Date();
const sevenDaysBefore = new Date(startDate);
sevenDaysBefore.setDate(startDate.getDate() - 7);

export default {
  // 4. Removed 'mixins: [ConsentForMixin]' entirely
  components: {
    Loading,
    VueGoodTable,
    Datepicker,
    SurveySummary,
    ConsentFormModals
  },
  data() {
    return {
      showModal: false,
      showConsentModal: false,
      showEmailModal: false,
      assessmentDate: "",
      questions: "",
      currentSurveyId: null,

      isLoading: false,
      startDate: sevenDaysBefore,
      endDate: new Date(),
      surveyClass: "0",
      email: "",
      referenceNo: "",

      currentRowSystemType: 0,

      experience: "",
      undergraduateYear: "",
      graduationYear: "",
      countryOfQualification: "",
      areaOfPractice: "",
      havePostgraduateEndodontics: "",
      postgraduateEndodonticsExperience: "",
      postgraduateEndodonticsYear: "",
      postgraduateEndodonticsExperienceOther: "",
      isSimulated: "-1",

      rows: [],
      columns: [
        { label: 'Date', field: 'createdAt' },
        { label: 'Score', field: 'score' },        
        { label: 'Class', field: 'surveyClass' },
        { label: 'Other Class', field: 'otherSurveyClass' },
        { label: 'Referrer Email', field: 'email' },
        { label: 'Reference', field: 'reference' },
        { label: 'Patient', field: 'simulatedPatient' },
        { label: 'Patient Name', field: 'patientName' },
        { label: 'Patient Email', field: 'patientEmail' },
        { label: 'Patient Contact', field: 'patientContact' },
        { label: 'Outcome', field: 'btn' },
        { label: 'Referral', field: 'downloadSurvey' },
        { label: 'Consent', field: 'downloadConsent' },
        { label: 'Send Information', field: 'sendLeaflet' },
      ],

      emailToSend: "",
      // Added consentForm here as parent needs to hold the state passed as props
      consentForm: {
        surveyId: null,
        patientName: '',
        patientEmail: '',
        classification: '',
        procedure: '',
        briefExplanation: '',
        intendedBenefits: '',
        complexities: '',
        specificRisks: '',
        significantRisks: '',
        alternatives: ''
      }
    }
  },

  mounted() {
    if (this.user == null) {
      this.$router.push("/login");
      return;
    }
    this.search();
  },

  methods: {
    sendInformation(survey) {

      if (!survey || !survey.patientEmail) {
        alert('Patient email not found for this survey');
        return;
      }

      // Confirm before sending
      if (!confirm(`Send information leaflet to ${survey.patientEmail}?`)) {
        return;
      }

      this.isLoading = true;

      this.axios.post("/api/survey/SendInformationLeaflet", {
        surveyId: survey.surveyId
      }, {
        headers: {
          'Authorization': 'Bearer ' + this.user.token,
          'Content-Type': 'application/json'
        }
      }).then((response) => {
        alert('Information leaflet sent successfully!');
        this.isLoading = false;
      }).catch((error) => {
        console.error('Error sending information leaflet:', error);
        alert('Failed to send information leaflet. Please try again.');
        this.isLoading = false;
      });
    },

    downloadSurvey(surveyId) {
      this.isLoading = true;
      this.axios.get("/api/survey/getSurveyPDF", {
        params: { id: surveyId },
        headers: {
          'Authorization': 'Bearer ' + this.user.token
        }
      }).then((response) => {
        const base64 = response.data;
        const binaryData = atob(base64);
        const byteArray = new Uint8Array(binaryData.length);
        for (let i = 0; i < binaryData.length; i++) {
          byteArray[i] = binaryData.charCodeAt(i);
        }

        const blob = new Blob([byteArray], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `survey-${surveyId}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        this.isLoading = false;
      }).catch(error => {
        console.error('Error downloading PDF:', error);
        this.isLoading = false;
      });
    },

    downloadConsent(surveyId) {
      this.isLoading = true;
      this.axios.get("/api/survey/getConsentPDF", {
        params: { id: surveyId },
        headers: {
          'Authorization': 'Bearer ' + this.user.token
        }
      }).then((response) => {
        const base64 = response.data;
        const binaryData = atob(base64);
        const byteArray = new Uint8Array(binaryData.length);
        for (let i = 0; i < binaryData.length; i++) {
          byteArray[i] = binaryData.charCodeAt(i);
        }

        const blob = new Blob([byteArray], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `consent-${surveyId}.dotx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        this.isLoading = false;
      }).catch(error => {
        console.error('Error downloading PDF:', error);
        this.isLoading = false;
      });
    },

    navToSurvey(surveyId) {
      this.isLoading = true;

      //this.$router.push({ path: `/admin/report/view/${surveyId}` })
      this.axios.get("/api/report/getbyid", {
        params: { surveyId: surveyId, },
        headers: {
          'Authorization': 'Bearer ' + this.user.token
        }
      }).then((response) => {
        var survey = response.data;
        this.assessmentDate = moment(survey.createdAt).format("DD MMM yyyy"),
          this.questions = survey.questions;
        // We need to select the responses as selected for our survey component to make it easier
        // And change the question to the title
        for (let question of this.questions) {
          question.title = question.question;
          for (let response of question.responses) {
            response.isSelected = true;
          }
        }

        this.isLoading = false;
        this.showModal = true;
      });
    },

    search() {
      this.axios.get("/api/report/getreferrals", {
        params: {
          startDate: this.startDate,
          endDate: this.endDate,
          surveyClass: this.surveyClass,
          experience: this.experience,
          email: this.email,
          referenceNo: this.referenceNo,
          undergraduateYear: this.experience == 'Student' ? this.undergraduateYear : "",
          graduationYear: this.experience == 'Qualified Dentist' ? this.graduationYear : "",
          countryOfQualification: this.experience == 'Qualified Dentist' ? this.countryOfQualification : "",
          areaOfPractice: this.experience == 'Qualified Dentist' ? this.areaOfPractice : "",
          havePostgraduateEndodontics: this.experience == 'Qualified Dentist' ? this.havePostgraduateEndodontics : "",
          postgraduateEndodonticsExperience: this.havePostgraduateEndodontics == 'true' ? this.postgraduateEndodonticsExperience : "",
          postgraduateEndodonticsExperienceOther: this.havePostgraduateEndodontics == 'true' && this.postgraduateEndodonticsExperience == 'Other' ? this.postgraduateEndodonticsExperienceOther : "",
          postgraduateEndodonticsYear: this.havePostgraduateEndodontics == 'true' ? this.postgraduateEndodonticsYear : "",
          isSimulated: this.isSimulated,
        },
        headers: {
          'Authorization': 'Bearer ' + this.user.token
        }
      }).then((response) => {
        this.rows = response.data;
      });
    }
  },
  computed: {
    user() {
      return authStore.state.user;
    }
  },
}
</script>

<style lang="scss" scoped>
.content-wrapper {
  h1 {
    font-size: 28px;
    background: #2A2A2E;
    color: #FFF;
    padding: 10px;
  }

  .table {
    padding: 10px;
  }

  .filters {
    display: grid;
    gap: 5px;
    grid-template-columns: 1fr 1fr 1fr 1fr;
    margin: 10px;

    .filter-group {
      display: flex;
      flex-direction: column;
      text-align: left;

      label {
        font-weight: bold;
        margin-bottom: 5px;
      }

      input,
      select {
        width: 100%;
        padding: 5px
      }
    }
  }
}

::v-deep .modal-container {
  display: flex;
  justify-content: center;
  align-items: center;
}

::v-deep .modal-content {
  position: relative;
  display: flex;
  flex-direction: column;
  margin: 0 1rem;
  padding: 1.3rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.25rem;
  background: #fff;
  width: 80%;
  overflow-y: auto;
  max-height: 90%;

  img {
    width: 100%;
  }
}

::v-deep .consent-modal-content {
  max-width: 600px;
}

::v-deep .email-modal-content {
  max-width: 400px;
  text-align: center;
}

.modal__title {
  margin: 0 2rem 0 0;
  font-size: 1.5rem;
  font-weight: 700;
}

.modal__close {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  background: transparent;
  border: 0px;
  outline: 0px;
  font-size: 32px;
}

.consent-title {
  font-size: 20px;
  margin-top: 0;
  margin-bottom: 20px;
  text-align: center;
  color: #111827;
}

.consent-form {
  .form-group {
    margin-bottom: 15px;
    text-align: left;

    label {
      display: block;
      margin-bottom: 5px;
      font-weight: 500;
      color: #374151;
    }

    .form-input,
    .form-textarea {
      width: 100%;
      padding: 8px 12px;
      border: 1px solid #D1D5DB;
      border-radius: 4px;
      font-size: 14px;
      transition: all 0.3s ease;

      &:focus {
        border-color: #013B82;
        box-shadow: 0 0 0 2px rgba(1, 59, 130, 0.1);
        outline: none;
      }
    }

    .form-textarea {
      resize: vertical;
      font-family: inherit;
    }
  }
}

.consent-actions,
.email-actions {
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-top: 20px;
}

.btn-primary,
.btn-secondary {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 20px;
  border-radius: 6px;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.2s ease;
  cursor: pointer;
  border: none;

  .btn-icon {
    flex-shrink: 0;
  }
}

.btn-primary {
  background: linear-gradient(135deg, #013B82, #0A2D5C);
  color: white;
  box-shadow: 0 2px 4px rgba(1, 59, 130, 0.2);

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 3px 6px rgba(1, 59, 130, 0.3);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 1px 3px rgba(1, 59, 130, 0.2);
  }
}

.btn-secondary {
  background: white;
  color: #4B5563;
  border: 1px solid #D1D5DB;

  &:hover {
    background: #F9FAFB;
    color: #111827;
    border-color: #9CA3AF;
  }

  &:active {
    background: #F3F4F6;
  }
}

.btn {
  cursor: pointer;
  padding: 6px 12px;
  background-color: #013B82;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  transition: background-color 0.2s;

  &:hover {
    background-color: #0056b3;
  }
}

.email-form-group {
  margin-bottom: 15px;
  text-align: left;

  label {
    display: block;
    margin-bottom: 5px;
    font-weight: 500;
    color: #374151;
  }

  .form-input {
    width: 100%;
    padding: 8px 12px;
    border: 1px solid #D1D5DB;
    border-radius: 4px;
    font-size: 14px;
    transition: all 0.3s ease;

    &:focus {
      border-color: #013B82;
      box-shadow: 0 0 0 2px rgba(1, 59, 130, 0.1);
      outline: none;
    }
  }
}

::v-deep .email-modal-content {
  max-width: 450px;
  text-align: center;
}
</style>